import React from 'react';
import Navbar from './Navbar';
import Footer from './Footer';

const AboutPage: React.FC = () => {
  const values = [
    {
      icon: (
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
          <circle cx="32" cy="32" r="30" stroke="#4452e3" strokeWidth="4" fill="none" />
          <path d="M20 32L28 40L44 24" stroke="#4452e3" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      title: 'Speed',
      subtitle: 'We deliver in days, not months',
    },
    {
      icon: (
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
          <rect x="16" y="16" width="32" height="32" rx="4" stroke="#4452e3" strokeWidth="4" fill="none" />
          <path d="M24 28L30 34L40 24" stroke="#4452e3" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      title: 'Quality',
      subtitle: 'Enterprise-grade solutions, always',
    },
    {
      icon: (
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
          <path d="M32 16L40 24L32 32L24 24L32 16Z" stroke="#4452e3" strokeWidth="4" fill="none" strokeLinejoin="round" />
          <path d="M16 32L24 40L32 32L24 24L16 32Z" stroke="#4452e3" strokeWidth="4" fill="none" strokeLinejoin="round" />
          <path d="M32 32L40 40L32 48L24 40L32 32Z" stroke="#4452e3" strokeWidth="4" fill="none" strokeLinejoin="round" />
          <path d="M48 32L40 40L32 32L40 24L48 32Z" stroke="#4452e3" strokeWidth="4" fill="none" strokeLinejoin="round" />
        </svg>
      ),
      title: 'Partnership',
      subtitle: 'Your success is our success',
    },
    {
      icon: (
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
          <circle cx="32" cy="24" r="8" stroke="#4452e3" strokeWidth="4" fill="none" />
          <path d="M32 32V48" stroke="#4452e3" strokeWidth="4" strokeLinecap="round" />
          <circle cx="32" cy="48" r="4" fill="#4452e3" />
        </svg>
      ),
      title: 'Innovation',
      subtitle: 'Continually pushing boundaries',
    },
  ];

  return (
    <div className="w-full bg-white">
      <Navbar />

      {/* Hero Section — dark gradient band with empty space below for the overlapping banner */}
      <section
        className="pt-16 lg:pt-20 pb-12 lg:pb-56 relative"
        style={{
          background:
            'radial-gradient(55% 80% at 8% 100%, rgba(120,102,214,0.35) 0%, rgba(34,37,59,0) 55%), #22253b',
        }}
      >
        <div className="max-w-content mx-auto px-6 relative z-10">
          <h1 className="font-exo font-bold text-white text-3xl lg:text-[48px] tracking-[2.4px] leading-tight mb-4">
            About Us
          </h1>
          <p className="font-proxima text-white text-base lg:text-lg tracking-[0.8px] leading-normal">
            Get in touch with our team to discuss your project needs
          </p>
          {/* Banner sits on the dark hero, below the text — mobile only */}
          <img
            src={require('../assets/img/about/banner.png')}
            alt="About Us"
            className="lg:hidden w-full h-auto rounded-lg shadow-2xl ring-1 ring-white/10 mt-8"
          />
        </div>
      </section>

      {/* Our Story Section — banner is pulled up so it straddles the dark/white edge (3-D effect) */}
      <section className="pt-16 lg:pt-24 pb-16 lg:pb-24 bg-white">
        {/* Banner Image — top half over the dark hero, bottom half over the white section */}
        <div className="max-w-content mx-auto px-6">
          <div className="hidden lg:block relative z-20 lg:-mt-72 mb-10 lg:mb-16">
            <img
              src={require('../assets/img/about/banner.png')}
              alt="About Us"
              className="w-full h-auto rounded-lg shadow-2xl ring-1 ring-white/10"
            />
          </div>
        </div>

        <div className="max-w-content mx-auto px-6 pb-8">
          <h2 className="font-exo font-bold text-ebony-clay text-2xl lg:text-[38px] tracking-[1.9px] leading-normal mb-4">
            Our Story
          </h2>
          <p className="font-proxima text-black text-base lg:text-lg tracking-[0.8px] leading-relaxed mb-4">
            Founded over 15 years ago, MetaLogix Solutions has been at the forefront of digital transformation, helping businesses leverage technology to achieve their goals. We've evolved from traditional software development to pioneering{' '}
            <span className="font-proxima-bold font-bold">Document Centric AI Development™</span>, a revolutionary methodology that delivers solutions 10x faster than conventional approaches.
          </p>
        </div>

        <div className="max-w-content mx-auto px-6">
          <h2 className="font-exo font-bold text-ebony-clay text-2xl lg:text-[38px] tracking-[1.9px] leading-normal mb-4">
            Our Mission
          </h2>
          <p className="font-proxima text-black text-base lg:text-lg tracking-[0.8px] leading-relaxed">
            To transform businesses through intelligent technology solutions that are delivered faster, work better, and scale effortlessly.
          </p>
        </div>
      </section>

      {/* Document Centric AI Development Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-content mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">
            {/* Left: Content */}
            <div className="flex-1">
              <h2 className="font-exo font-bold text-royal-blue text-2xl lg:text-[38px] tracking-[1.9px] leading-normal mb-6">
                Document Centric AI Development™
              </h2>
              <p className="font-proxima text-black text-base lg:text-lg tracking-[0.8px] leading-relaxed mb-6">
                Our proprietary methodology treats documentation as the source of truth, using AI to generate, validate, and optimize code directly from specifications.
              </p>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="font-proxima-bold font-bold text-ebony-clay text-lg">10x</span>
                  <span className="font-proxima text-black text-base lg:text-lg tracking-[0.8px] leading-normal">faster development cycles</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="font-proxima-bold font-bold text-ebony-clay text-lg">95%</span>
                  <span className="font-proxima text-black text-base lg:text-lg tracking-[0.8px] leading-normal">first-time accuracy</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="font-proxima-bold font-bold text-ebony-clay text-lg">100%</span>
                  <span className="font-proxima text-black text-base lg:text-lg tracking-[0.8px] leading-normal">documentation coverage</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="font-proxima-bold font-bold text-ebony-clay text-lg">90%</span>
                  <span className="font-proxima text-black text-base lg:text-lg tracking-[0.8px] leading-normal">reduction in time to market</span>
                </div>
              </div>
            </div>

            {/* Right: Badge/Icon */}
            <div className="flex-shrink-0 w-full lg:w-auto flex justify-center lg:justify-end">
              <div className="w-[200px] h-[200px] lg:w-[250px] lg:h-[250px] relative">
                <div className="absolute inset-0 bg-royal-blue/10 rounded-lg"></div>
                <img
                  src={require('../assets/img/about/badge.png')}
                  alt="Document Centric AI Development"
                  className="w-full h-full object-contain p-8"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-content mx-auto px-6">
          <h2 className="font-exo font-bold text-ebony-clay text-2xl lg:text-[38px] tracking-[1.9px] leading-normal mb-12 text-center">
            Our Values
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {values.map((value, index) => (
              <div key={index} className="flex flex-col items-center text-center bg-gray-50 p-6 lg:p-8 rounded-lg">
                <div className="mb-6">{value.icon}</div>
                <h3 className="font-exo font-bold text-ebony-clay text-xl lg:text-2xl tracking-[1.2px] leading-normal mb-2">
                  {value.title}
                </h3>
                <p className="font-proxima text-black text-base tracking-[0.8px] leading-normal">
                  {value.subtitle}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ready to Transform CTA - copied from BookMeetingCTA */}
      <section className="bg-royal-blue py-12 lg:py-16 relative overflow-hidden">
        {/* Decorative circles — hidden on mobile */}
        <div className="absolute left-4 lg:left-[227px] top-1/2 -translate-y-1/2 w-[336px] h-[336px] pointer-events-none hidden lg:block">
          <div
            className="absolute inset-0 rounded-full opacity-5"
            style={{ background: 'radial-gradient(50% 50% at -29% 62%, rgba(137,146,208,1) 3%, rgba(255,255,255,1) 100%)' }}
          />
          <div
            className="absolute left-[44px] top-[44px] w-[248px] h-[248px] rounded-full opacity-5"
            style={{ background: 'radial-gradient(50% 50% at -29% 62%, rgba(137,146,208,1) 3%, rgba(255,255,255,1) 100%)' }}
          />
          <div className="absolute left-[72px] top-[69px] w-[197px] h-[197px] rounded-full bg-[#4452e340] blur-[18px]" />
        </div>

        <div className="max-w-content mx-auto px-6 flex flex-col lg:flex-row items-center gap-8 lg:gap-12 relative z-10">
          {/* Text content — left side */}
          <div className="flex-1">
            <h2 className="font-exo font-black text-white text-3xl lg:text-[42px] tracking-[2.1px] leading-tight">
              Ready to Transform Your Business?
            </h2>
            <p className="font-proxima text-white text-lg lg:text-2xl tracking-[1.2px] leading-normal mt-6">
              Get your project quotation in just <span className="font-proxima-bold font-bold">3 days</span>
            </p>
            <a
              href="#get-started"
              className="inline-flex items-center justify-center rounded-md overflow-hidden bg-white cursor-pointer py-3 px-5 lg:py-4 lg:px-6 hover:bg-opacity-90 transition-colors mt-8"
            >
              <span className="font-proxima-bold font-bold text-royal-blue text-base lg:text-2xl leading-6 whitespace-nowrap">
                GET STARTED
              </span>
            </a>
          </div>

          {/* Meeting photo — right side */}
          <div className="flex-shrink-0 w-full max-w-[220px] lg:max-w-[425px] mx-auto lg:mx-0">
            <img
              className="w-full h-auto object-cover rounded"
              src={require('../assets/img/book-meeting/meeting-photo.png')}
              alt="Team meeting"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
